// A leaderboard row (an <li>): rank, movement badge from prevRank, name with a streak flame, score that counts up by
// `delta`. The "you" row is highlighted in teal. Rows glide to their new places with Motion `layout` (FLIP).
import { motion } from 'motion/react';
import type { LeaderboardRow } from '@shared/types';
import { CountUp } from '@/motion/CountUp';
import { springs } from '@/motion/springs';
import { useReducedMotion } from '@/motion/reduced';
import { MovementBadge } from './MovementBadge';
import { StreakFlame } from './StreakFlame';

/** showMovement: draws ▲/▼/–/New from row.prevRank. */
export function RankRow({ row, me, showMovement = true, delta, className = '' }: {
  row: LeaderboardRow; me?: boolean; showMovement?: boolean;
  /** Points gained since the last board: shown as +n and counted into the score. */
  delta?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const streak = row.streak ?? 0;
  return (
    <motion.li layout={reduced ? false : 'position'} initial={reduced ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={springs.layout}
      className={`rd-rank ${className}`} data-me={me || undefined} data-pos={row.rank >= 1 && row.rank <= 3 ? row.rank : undefined}
      style={showMovement ? undefined : { gridTemplateColumns: '2.25rem minmax(0,1fr) auto' }}>
      <span className="rd-rank-pos">{row.rank}</span>
      {showMovement && <MovementBadge rank={row.rank} prevRank={row.prevRank} />}
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate font-semibold text-ink" title={row.name}>{row.name}</span>
        {me && <span className="shrink-0 rounded-md bg-brand px-2 py-0.5 text-[0.7rem] font-bold text-white">You</span>}
        {streak >= 2 && <StreakFlame n={streak} size={16} className="shrink-0" />}
      </span>
      <span className="flex items-baseline gap-2 justify-self-end">
        {delta ? <span className="numeral text-sm text-correct-strong"><span className="sr-only">gained </span>+{delta.toLocaleString('en-GB')}</span> : null}
        <CountUp value={row.score} from={delta ? row.score - delta : undefined} className="text-lg text-ink" />
      </span>
    </motion.li>
  );
}
